export type ArticleSection = {
  heading: string;
  paragraphs: string[];
  list?: string[];
};

export type Article = {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  cover: string;
  author: string;
  date: string;
  readMinutes: number;
  heat: number;
  tags: string[];
  lead: string;
  sections: ArticleSection[];
  verdict: string;
};

export const articles: Article[] = [
  {
    id: "a1",
    title: "决赛夜爆冷背后：三张评分表为什么对不上同一套口径",
    excerpt:
      "同一场决赛，现场公示分、组委会留档分和赛后通稿分出现了三种结果。我们逐张比对了口径差异，找到了名次被改写的那一行。",
    category: "score",
    cover:
      "https://readdy.ai/api/search-image?query=Elegant%20empty%20theatre%20stage%20with%20warm%20spotlight%20and%20red%20velvet%20curtains%2C%20scoring%20podium%20in%20shadow%2C%20editorial%20documentary%20photography%2C%20moody%20amber%20and%20crimson%20tones%2C%20cinematic%20composition&width=1200&height=800&seq=pageant-cover-a1&orientation=landscape",
    author: "深扒社编辑部",
    date: "2026-08-28",
    readMinutes: 9,
    heat: 98,
    tags: ["评分猫腻", "决赛复盘", "口径差异"],
    lead:
      "一份评分表可以有多个版本，但一场比赛的冠军只能有一个。真正值得追问的不是谁拿奖，而是同一套规则在不同表格里被解释成了什么样子。",
    sections: [
      {
        heading: "三张表，三个名次",
        paragraphs: [
          "决赛结束当晚，现场公示栏贴出的名次是 A 组选手第一。半小时后，组委会内部留档的电子表把第一名换成了 B 组选手。第二天凌晨发布的通稿里，第一名又回到了 A 组，但第三名悄悄换了人。",
          "把三张表放在一起会发现，差异并非来自加总错误，而是来自一项名为“形体表现”的分项在留档表里被拆成了两栏，而现场公示时这两栏被合并后重新平均。",
        ],
        list: [
          "现场公示分：五项加权，形体表现合并计一栏，权重 20%",
          "留档电子分：形体表现拆为“台步”与“镜头感”两栏，各占 10%",
          "通稿引用分：直接引用留档分，但删去了两栏拆分说明",
        ],
      },
      {
        heading: "被拆开的那一栏，恰好决定了名次",
        paragraphs: [
          "A 组选手在“台步”一项上有明显优势，B 组选手在“镜头感”上更强。合并计时两人几乎同分，拆分后 B 组以微弱优势领先。也就是说，拆分动作本身就足以改写冠军。",
          "这里并不必然存在主观恶意，但一个专业赛事的评分口径在赛后两小时内被调整而没有任何公示说明，已经构成了程序上的漏洞。观众看到的不是黑箱，而是一扇没锁的门。",
        ],
      },
      {
        heading: "如何自己复核一场比赛的评分",
        paragraphs: [
          "普通观众很难拿到留档表，但可以从公开物料里倒推。第一步比对各渠道公布的分项名称是否一致；第二步检查每一项权重加起来是否等于 100%；第三步看并列名次的处理规则有没有提前写明。",
          "只要这三步里有两步对不上，就说明这场比赛的计分流程存在可被操作的空间。这比任何截图爆料都更接近事实。",
        ],
      },
    ],
    verdict:
      "口径被调整不等于名次被内定，但未公示的调整就是可以被利用的缝隙。判断一场比赛是否干净，先看规则有没有在赛前锁死。",
  },
  {
    id: "a2",
    title: "评委名单临开赛前微调，席位背后是签约关系图",
    excerpt:
      "开赛前一天，两位评委被替换。顺着任期、经纪约与赞助品牌三条线梳理，能看到一张比评分表更早落笔的关系图。",
    category: "deal",
    cover:
      "https://readdy.ai/api/search-image?query=Judges%20table%20with%20name%20cards%20and%20closed%20folders%20in%20a%20dim%20elegant%20hall%2C%20single%20empty%20chair%20pulled%20out%2C%20editorial%20documentary%20photography%2C%20warm%20amber%20light%20and%20deep%20shadow%2C%20cinematic%20composition&width=1200&height=800&seq=pageant-cover-a2&orientation=landscape",
    author: "深扒社编辑部",
    date: "2026-08-22",
    readMinutes: 8,
    heat: 92,
    tags: ["评委", "幕后交易", "签约关系"],
    lead:
      "评委的评分只占一瞬，但谁来当评委往往在几个月前就决定了结果的上限。临阵换人之所以敏感，是因为它换掉的不只是一个名字。",
    sections: [
      {
        heading: "临阵换人，换掉的到底是什么",
        paragraphs: [
          "两位被替换的评委都属于“独立评审”类别，按章程不持有任何参赛选手的经纪约。替换进来的两位，一位曾在三年前担任过某参赛机构的外聘导师，另一位所在公司是本届的赞助方之一。",
          "单项行为本身不违规，章程也没有禁止赞助方派代表进入评审席。但当赞助方代表与争议选手所属机构存在历史交集时，回避机制是否触发就成了关键问题。",
        ],
      },
      {
        heading: "回避机制为什么常常形同虚设",
        paragraphs: [
          "多数赛事确实写了回避条款，但回避的判定主体往往就是组委会自己。选手和观众没有申述入口，媒体也拿不到评委的完整利益披露。",
          "结果是：制度存在，执行不透明。这正是“幕后交易”传闻永远有市场的原因——不是大家相信阴谋，而是没有人能证明它不存在。",
        ],
        list: [
          "评委利益披露应在赛前公示，而非赛后补档",
          "涉及赞助方与参赛机构交集时，应有第三方介入裁定回避",
          "选手应有匿名申述通道，避免申诉影响自身名次",
        ],
      },
      {
        heading: "普通观众能看什么",
        paragraphs: [
          "看评委构成比看席位数更有意义。如果一家赞助机构在近三届比赛中都派出了评委，而该机构的参赛选手又屡屡进入前列，这本身就是一个值得追问的统计现象。",
          "统计不等于指控。但把连续多次的巧合摊开来看，它至少说明现有的回避设计需要更新。",
        ],
      },
    ],
    verdict:
      "换评委不等于交易，但利益披露缺失会让每一次正常调整都看起来像交易。透明是唯一的解药。",
  },
  {
    id: "a3",
    title: "同分不同命：并列名次的处理规则里藏着多少自由裁量",
    excerpt:
      "两位选手总分完全相同，一个进了前三，一个止步初赛。差异不在分数，而在“同分怎么办”这一行没写清楚的字。",
    category: "score",
    cover:
      "https://readdy.ai/api/search-image?query=Two%20identical%20score%20sheets%20side%20by%20side%20on%20a%20dark%20wooden%20table%20with%20a%20brass%20pen%2C%20warm%20lamp%20light%2C%20editorial%20documentary%20still%20life%2C%20amber%20and%20crimson%20tones%2C%20shallow%20depth%20of%20field&width=1200&height=800&seq=pageant-cover-a3&orientation=landscape",
    author: "深扒社编辑部",
    date: "2026-08-15",
    readMinutes: 7,
    heat: 85,
    tags: ["评分猫腻", "并列名次", "规则漏洞"],
    lead:
      "规则最怕的不是被违反，而是被留白。留白之处，任何解释都成立，也任何解释都不成立。",
    sections: [
      {
        heading: "一句“由评委会裁定”能解决多少问题",
        paragraphs: [
          "本届赛程里，同分处理条款只有一句：同分时由评委会裁定。没有说明是重新投票、比较最高分项，还是参考初赛成绩。",
          "这意味着当两人同分时，结果完全取决于现场那几分钟的自由裁量，而自由裁量恰恰是最难被复核的部分。",
        ],
      },
      {
        heading: "更常见的三种做法",
        paragraphs: [
          "成熟的赛事通常会给出明确的二级比较规则，让同分的结果可以被推导出来，而不是被宣布出来。",
        ],
        list: [
          "优先比较权重最高的分项得分，逐项下探直到分出高低",
          "并列者共同进入下一轮，扩大名额而非压缩名额",
          "引入第三方监督员复核后当场宣布，过程留痕",
        ],
      },
      {
        heading: "留白为什么总是对某些人更有利",
        paragraphs: [
          "留白本身是中性词，但留白对不同人的价值不一样。对准备充分的选手，留白意味着风险；对信息更灵通的人，留白意味着空间。",
          "所以讨论“同分不同命”时，重点不该是某一位选手是否受了委屈，而应该是章程能否在赛前就把留白填上。",
        ],
      },
    ],
    verdict:
      "同分条款越模糊，解释权越集中。把自由裁量压缩到最小，是赛事取信于人的第一步。",
  },
  {
    id: "a4",
    title: "现场举牌与最终名次之间，为什么会错开十二秒",
    excerpt:
      "直播回放显示，最后一位评委举牌到主持人念出名字，中间隔了十二秒。这十二秒里发生了什么，决定了名次的可信度。",
    category: "cold",
    cover:
      "https://readdy.ai/api/search-image?query=Backstage%20monitor%20screen%20showing%20a%20stage%20with%20counting%20delay%2C%20cables%20and%20clapperboard%20on%20desk%2C%20editorial%20documentary%20photography%2C%20warm%20amber%20light%20with%20deep%20shadows%2C%20cinematic%20mood&width=1200&height=800&seq=pageant-cover-a4&orientation=landscape",
    author: "深扒社编辑部",
    date: "2026-08-09",
    readMinutes: 6,
    heat: 81,
    tags: ["爆冷瞬间", "现场流程", "直播复盘"],
    lead:
      "大型晚会都有延迟，但延迟要用在技术上，而不是用在结果上。十二秒不长，却足够让一个名次从表格走进另一间屋子。",
    sections: [
      {
        heading: "正常的十二秒应该用来做什么",
        paragraphs: [
          "计分员把纸质分录入系统、复核、与备用纸质分交叉校验、确认同分处理，这些流程合计大约需要十到二十秒。流程本身合理。",
          "问题在于，观众看到的画面里，这十二秒没有字幕说明，也没有复核特写。透明流程被打包成了一个空白。",
        ],
      },
      {
        heading: "异常出现在哪一步",
        paragraphs: [
          "把回放逐帧比对后可以发现，举牌结束后主持人并没有立刻收到耳返指令，而是先伸手拿到了另一张手卡。这说明名次并非现场即时产生，而是提前写好的。",
          "提前准备手卡在流程上无可厚非，但当举牌结果与手卡内容存在调整空间时，整个“现场感”就变成了表演。",
        ],
      },
      {
        heading: "观众最该被公示的三样东西",
        paragraphs: [
          "能不能把复核过程拍给观众看，是检验一场直播是否坦荡的简单标准。",
        ],
        list: [
          "计分复核的实际画面，而非礼仪镜头",
          "同分或争议项的当场处理记录",
          "评委最终确认签字的时刻",
        ],
      },
    ],
    verdict:
      "十二秒不是证据，但它暴露了流程的不透明。把复核过程放进镜头，比任何声明都有说服力。",
  },
  {
    id: "a5",
    title: "纸质分与电子分两套并存，计分漏洞是怎么产生的",
    excerpt:
      "不少赛事仍保留纸质分作为“保险”。但当两套数据不一致时，该听谁的，恰恰是最容易做文章的地方。",
    category: "score",
    cover:
      "https://readdy.ai/api/search-image?query=Counting%20room%20with%20score%20sheets%20and%20a%20laptop%20showing%20a%20spreadsheet%2C%20calculator%20and%20coffee%20cup%2C%20editorial%20documentary%20photography%2C%20warm%20amber%20desk%20lamp%20and%20dark%20surroundings&width=1200&height=800&seq=pageant-cover-a5&orientation=landscape",
    author: "深扒社编辑部",
    date: "2026-08-02",
    readMinutes: 8,
    heat: 76,
    tags: ["评分猫腻", "计分流程", "数据复核"],
    lead:
      "双轨制不是问题，双轨制没有仲裁规则才是问题。当两本账打架，比赛结果就取决于谁先被拿出来引用。",
    sections: [
      {
        heading: "双轨制的本意是互相校验",
        paragraphs: [
          "纸质分是原始凭证，电子分是汇总工具。正常逻辑下，电子分应与纸质分完全一致，不一致就必须当场查明原因。",
          "但很多赛事的章程只写了“以电子分为准”，却没有规定复核责任人和复核时限，这等于把校验环节悬空了。",
        ],
      },
      {
        heading: "漏洞在这里被打开",
        paragraphs: [
          "如果电子分录入后缺乏二次核对，那么录入环节就成了唯一的入口。谁录入、什么时候录、有没有留痕，决定了这套系统能不能被信任。",
        ],
        list: [
          "录入与复核应由不同人员完成，不能一人包办",
          "纸质分应扫描存档，与电子分同源可查",
          "任何事后修改都需二次签字并记录时间戳",
        ],
      },
      {
        heading: "对观众来说，能追问什么",
        paragraphs: [
          "与其争论谁被黑幕，不如追问一句“纸质分去哪了”。公开原始凭证是最低成本的信任修复方式，这也是很多赛事始终回避的动作。",
        ],
      },
    ],
    verdict:
      "两套数据并存不是漏洞，没有仲裁规则才是。原始凭证公开与否，直接决定结果能不能被相信。",
  },
  {
    id: "a6",
    title: "赞助席位与奖项归属，隐蔽关联该如何被验证",
    excerpt:
      "把最近三届的赞助品牌、评委来源与获奖名单叠在一起看，会发现一些不适合用巧合解释的重复。",
    category: "deal",
    cover:
      "https://readdy.ai/api/search-image?query=Elegant%20sponsor%20banners%20and%20trophy%20on%20a%20dark%20stage%2C%20golden%20confetti%20in%20warm%20light%2C%20editorial%20documentary%20photography%2C%20crimson%20and%20amber%20tones%2C%20cinematic%20composition&width=1200&height=800&seq=pageant-cover-a6&orientation=landscape",
    author: "深扒社编辑部",
    date: "2026-07-26",
    readMinutes: 7,
    heat: 73,
    tags: ["幕后交易", "赞助关系", "数据交叉"],
    lead:
      "赞助是赛事活下去的前提，这没什么不光彩。不光彩的是当赞助影响力悄悄越过了评审边界，却没有任何披露。",
    sections: [
      {
        heading: "把三层数据叠起来看",
        paragraphs: [
          "第一层是赞助品牌名单，第二层是评委任职机构，第三层是获奖选手所属机构。单独看每一层都很正常，叠在一起就会出现重复的坐标。",
        ],
        list: [
          "某品牌连续三届赞助，其外聘导师连续三届出现在评委席",
          "该品牌关联机构的选手在三届中有两届进入前三",
          "三届的回避裁定均无书面记录",
        ],
      },
      {
        heading: "统计现象与指控之间的那条线",
        paragraphs: [
          "必须说清楚：重复出现不等于交易存在。样本量小、行业圈子窄，都可能导致巧合。但正因为无法排除巧合，才更需要利益披露制度。",
          "披露不是为了定罪，而是为了让巧合可以被解释。没有披露，所有解释都只能停留在猜测层面。",
        ],
      },
      {
        heading: "一份合格的利益披露应该包含什么",
        paragraphs: [
          "披露的价值在于可核查，因此字段必须具体，而不是一句“无利益冲突”。",
        ],
        list: [
          "评委近三年的任职与合作机构清单",
          "与参赛机构存在的任何商业往来",
          "在评分相关环节中的回避记录",
        ],
      },
    ],
    verdict:
      "赞助本身不是原罪，不披露才是。当关联可以被查到，巧合就不再是唯一解释。",
  },
  {
    id: "a7",
    title: "退赛声明为什么总在凌晨发出：一份合约里的时间条款",
    excerpt:
      "选手退赛的时间点往往不是偶然。顺着合约里的保密期与违约金条款，能看到声明时间的另一种解释。",
    category: "rule",
    cover:
      "https://readdy.ai/api/search-image?query=Closed%20contract%20folder%20with%20a%20wax%20seal%20and%20a%20pen%20on%20a%20dark%20desk%2C%20single%20desk%20lamp%20glow%2C%20editorial%20documentary%20still%20life%2C%20warm%20amber%20tones%20with%20deep%20shadow&width=1200&height=800&seq=pageant-cover-a7&orientation=landscape",
    author: "深扒社编辑部",
    date: "2026-07-19",
    readMinutes: 8,
    heat: 69,
    tags: ["合约规则", "退赛", "保密条款"],
    lead:
      "凌晨发布的声明不是文学选择，而是流程产物。看懂时间条款，就看懂了为什么很多话必须在某个时刻之前说完。",
    sections: [
      {
        heading: "时间条款在保护谁",
        paragraphs: [
          "多数选美合约里都有一条赛事保密期，规定选手不得在赛事进行期间或结束后的一定期限内发表可能影响赛事声誉的言论。违约金通常与名次挂钩。",
          "于是当选手想要表达异议时，最理性的时间点往往落在两个截止时间之间的缝隙里——既已过赛事核心期，又仍在保密期内。",
        ],
      },
      {
        heading: "声明文本里常见的三个动作",
        paragraphs: [
          "翻看近两年的退赛与异议声明，措辞高度相似，这不是抄袭，而是风险控制的共同结果。",
        ],
        list: [
          "使用“个人原因”等中性表述，避免触及赛事责任",
          "强调感谢与祝福，降低违约风险",
          "把具体细节留给私下沟通或后续采访",
        ],
      },
      {
        heading: "读者应该怎么读一份声明",
        paragraphs: [
          "声明里没写的，往往比写的更重要。缺失的部分包括具体条款、沟通过程、以及是否申请过复核。",
          "当一份声明只有情绪没有流程，它更像是一份合规文件，而不是一次表达。",
        ],
      },
    ],
    verdict:
      "凌晨不是巧合，是条款的产物。读懂时间线，比解读措辞更接近真相。",
  },
  {
    id: "a8",
    title: "网络票选刷量：机器票与人工票的识别边界在哪",
    excerpt:
      "人气奖被称为最透明的奖项，但票数曲线的异常波动说明，透明的前提是数据能被验证。",
    category: "vote",
    cover:
      "https://readdy.ai/api/search-image?query=Smartphone%20showing%20a%20rising%20vote%20chart%20on%20a%20dark%20desk%20with%20notebook%20and%20coffee%2C%20editorial%20documentary%20photography%2C%20warm%20amber%20light%20and%20deep%20shadows%2C%20cinematic%20mood&width=1200&height=800&seq=pageant-cover-a8&orientation=landscape",
    author: "深扒社编辑部",
    date: "2026-07-12",
    readMinutes: 7,
    heat: 66,
    tags: ["网络票选", "数据异常", "人气奖"],
    lead:
      "网络投票看起来最公平，因为每个人都能参与。但如果参与方式可以被批量复制，公平就只剩下形式。",
    sections: [
      {
        heading: "异常曲线长什么样",
        paragraphs: [
          "正常的人气增长是波浪形的，跟随节目播出与社交传播起伏。异常增长则表现为短时间内的陡峭直线，并且集中在深夜低活跃时段。",
          "单看总量很难发现问题，必须看增量的时间分布。这也是手工刷量与自动化脚本最容易暴露的共同特征。",
        ],
      },
      {
        heading: "三种常见手段与识别线索",
        paragraphs: [
          "投票机制不同，刷量方式也不同。识别线索通常藏在设备与时间维度里。",
        ],
        list: [
          "批量注册：同一设备指纹短时间多次投票",
          "自动化脚本：投票间隔呈现机器级规律性",
          "互投群：票数在不同选手之间同步涨落",
        ],
      },
      {
        heading: "主办方应该公布什么",
        paragraphs: [
          "如果人气奖真的想取信于人，至少要公布有效票的判定规则、异常票的剔除数量，以及第三方审计结论。",
          "只公布一个总数，等于把最容易被质疑的部分藏了起来。",
        ],
      },
    ],
    verdict:
      "人气奖的公信力不来自票多，而来自票能被验证。公布剔除规则，比公布总数更有价值。",
  },
  {
    id: "a9",
    title: "初赛到决赛分数为何会“技术性修正”",
    excerpt:
      "同一批选手，初赛与决赛的分数差距大得不合常理。所谓的技术性修正，到底修的是分数还是名次？",
    category: "score",
    cover:
      "https://readdy.ai/api/search-image?query=Two%20stacked%20documents%20with%20charts%20and%20numbers%20on%20a%20dark%20desk%20with%20a%20ruler%2C%20editorial%20documentary%20still%20life%2C%20warm%20amber%20light%20and%20deep%20shadow%2C%20cinematic%20composition&width=1200&height=800&seq=pageant-cover-a9&orientation=landscape",
    author: "深扒社编辑部",
    date: "2026-07-05",
    readMinutes: 6,
    heat: 62,
    tags: ["评分猫腻", "分数修正", "赛制设计"],
    lead:
      "分数应该反映表现，而不是反映赛程需要。当修正成为常规操作，观众看到的就不再是比赛，而是剧本的微调。",
    sections: [
      {
        heading: "修正通常出现在哪里",
        paragraphs: [
          "最常见的三种场景：初赛权重与决赛权重不同、初赛成绩按比例带入决赛、以及决赛前对分项权重临时调整。",
          "前两种如果在赛前写明，属于正常赛制设计；第三种如果没有公示，就属于事后修正。",
        ],
      },
      {
        heading: "为什么观众会觉得“看不懂”",
        paragraphs: [
          "因为主办方往往只展示最终名次，不展示分数是如何从初赛走到决赛的。缺乏过程，任何结果都显得可疑。",
        ],
        list: [
          "公布初赛到决赛的分数换算公式",
          "说明各阶段权重与带入比例",
          "对任何赛后调整给出书面理由",
        ],
      },
      {
        heading: "赛制设计的分寸",
        paragraphs: [
          "好的赛制让强者更稳，让黑马有可能。坏的赛制让结果提前确定，剩下的只是走完流程。",
          "判断标准很简单：如果把规则提前公开，还能不能得到同样的冠军。能，就是赛制；不能，就是操作。",
        ],
      },
    ],
    verdict:
      "修正本身不是问题，不公示的修正才是。公开换算过程，是赛制自证清白的最短路径。",
  },
  {
    id: "a10",
    title: "赛后通稿总在凌晨两点发布：传播节奏里的隐形操作",
    excerpt:
      "为什么负面讨论往往在通稿发布后才集中出现？发布时间本身就是一次传播管理。",
    category: "cold",
    cover:
      "https://readdy.ai/api/search-image?query=Press%20release%20draft%20on%20a%20desk%20with%20a%20clock%20showing%20two%20am%2C%20dark%20room%20with%20warm%20desk%20lamp%2C%20editorial%20documentary%20photography%2C%20amber%20and%20crimson%20tones%2C%20cinematic%20mood&width=1200&height=800&seq=pageant-cover-a10&orientation=landscape",
    author: "深扒社编辑部",
    date: "2026-06-28",
    readMinutes: 6,
    heat: 58,
    tags: ["传播节奏", "赛后通稿", "舆论管理"],
    lead:
      "凌晨发布不是媒体习惯，而是一种选择。选择在讨论最少的时段落子，本身就是对讨论的管理。",
    sections: [
      {
        heading: "凌晨发布能带来什么",
        paragraphs: [
          "深夜的舆论活跃度低，通稿可以获得一个相对干净的传播环境。等到第二天早晨，稿件已经完成第一轮扩散，负面讨论需要重新建立热度。",
          "这并不违规，但它是理解赛果争议为何常在数日后才发酵的关键背景。",
        ],
      },
      {
        heading: "被动回应与主动塑造",
        paragraphs: [
          "如果通稿只是在陈述结果，那是被动回应；如果通稿同时给出了对争议的解释框架，那就是主动塑造。",
        ],
        list: [
          "强调流程合规，弱化具体分数",
          "突出选手成长故事，转移讨论焦点",
          "引用往届数据，证明结果属于正常区间",
        ],
      },
      {
        heading: "读者如何保持判断力",
        paragraphs: [
          "看到通稿时，先看它回答了哪些问题，再看它回避了哪些问题。回避项通常就是真正需要追问的地方。",
          "传播节奏可以被设计，但事实不会因为发布时间的改变而改变。",
        ],
      },
    ],
    verdict:
      "发布时间是一种话语权。识别它，才能在信息节奏之外保留自己的判断。",
  },
];